import { toaster } from '@/components/ui/toaster'
import { type ReactElement, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'

import { refreshSelfAppliedTokenIfNeeded } from '@/api/selfAppliedAuth.ts'
import { oidKeycloakConfig } from '@/utils/oidConfig'
import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { User } from 'oidc-client-ts'

declare module 'axios' {
  interface AxiosRequestConfig {
    suppressForbiddenToast?: boolean
    skipAuthRedirect?: boolean
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

export const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/public',
  headers: {
    'Content-Type': 'application/json',
  },
})

export const selfAppliedApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL + '/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

export interface AxiosInterceptorProps {
  children: ReactElement
}

api.interceptors.response.clear()
api.interceptors.request.clear()
publicApi.interceptors.response.clear()
publicApi.interceptors.request.clear()

const handleRequest = async (
  req: InternalAxiosRequestConfig,
  auth?: { signinSilent: () => Promise<User | null> }
) => {
  let token = ''

  if ('authority' in oidKeycloakConfig) {
    const oidcStorage = localStorage.getItem(
      `oidc.user:${oidKeycloakConfig.authority}:${oidKeycloakConfig.client_id}`
    )
    if (oidcStorage) {
      const user = User.fromStorageString(oidcStorage)

      if (user.expired && auth) {
        await auth.signinSilent()
      }

      token = user?.access_token
    }
  }

  req.headers.Authorization = `Bearer ${token}`
  return req
}

const handleResponse = (response: AxiosResponse) => {
  return response
}

export const handleError = async (
  error: AxiosError,
  auth?: { signinRedirect: () => Promise<void> }
) => {
  const statusHttp = error.response?.status
  if (statusHttp === 403 || statusHttp === 401) {
    if (statusHttp === 401 && auth && !error.config?.skipAuthRedirect) {
      await auth.signinRedirect()
    }
    if (statusHttp === 403 && !error.config?.suppressForbiddenToast) {
      toaster.create({
        description: 'Você não possui permissão para utilizar este recurso.',
        type: 'error',
      })
    }
  }
  if (statusHttp === 400 || statusHttp === 500) {
    let { message } = error.response?.data as { message: string }
    if (Array.isArray(message)) {
      message = message.join(' ')
    }
    toaster.create({
      description: message,
      type: 'error',
    })
  }
  throw error
}

const reqInterceptor = async (req: InternalAxiosRequestConfig) => {
  return await handleRequest(req)
}

const reqInterceptorPublic = (req: InternalAxiosRequestConfig) => {
  return req
}

const authenticateSelfAppliedOtp = async (key: string, otp: string) => {
  const { data } = await publicApi.post<{ token: string }>(
    `collect/${key}/otp`,
    { otp }
  )
  return data
}

const reqInterceptorSelfApplied = async (req: InternalAxiosRequestConfig) => {
  const token = await refreshSelfAppliedTokenIfNeeded(
    authenticateSelfAppliedOtp
  )
  req.headers.Authorization = `Bearer ${token}`
  return req
}

const resInterceptor = (response: AxiosResponse) => {
  return handleResponse(response)
}

const errInterceptor = async (error: AxiosError) => {
  return await handleError(error)
}

const errInterceptorPublic = (error: AxiosError) => {
  return handleError(error)
}

api.interceptors.response.use(resInterceptor, errInterceptor)
publicApi.interceptors.response.use(resInterceptor, errInterceptorPublic)
selfAppliedApi.interceptors.response.use(resInterceptor, errInterceptorPublic)
api.interceptors.request.use(reqInterceptor)
publicApi.interceptors.request.use(reqInterceptorPublic)
selfAppliedApi.interceptors.request.use(reqInterceptorSelfApplied)

export const AxiosInterceptor = ({ children }: AxiosInterceptorProps) => {
  const auth = useAuth()

  useEffect(() => {
    api.interceptors.response.clear()
    api.interceptors.request.clear()
    publicApi.interceptors.response.clear()
    publicApi.interceptors.request.clear()
    selfAppliedApi.interceptors.response.clear()
    selfAppliedApi.interceptors.request.clear()

    const reqInterceptorId = api.interceptors.request.use((req) =>
      handleRequest(req, auth)
    )
    const reqInterceptorPublicId =
      publicApi.interceptors.request.use(reqInterceptorPublic)
    const reqInterceptorSelfAppliedId = selfAppliedApi.interceptors.request.use(
      reqInterceptorSelfApplied
    )
    const resInterceptorId = api.interceptors.response.use(
      handleResponse,
      (error: AxiosError<unknown, unknown>) => handleError(error, auth)
    )
    const resInterceptorPublicId = publicApi.interceptors.response.use(
      handleResponse,
      (error: AxiosError<unknown, unknown>) => handleError(error)
    )
    const resInterceptorSelfAppliedId =
      selfAppliedApi.interceptors.response.use(
        handleResponse,
        (error: AxiosError<unknown, unknown>) => handleError(error)
      )

    return () => {
      api.interceptors.request.eject(reqInterceptorId)
      api.interceptors.response.eject(resInterceptorId)
      publicApi.interceptors.request.eject(reqInterceptorPublicId)
      publicApi.interceptors.response.eject(resInterceptorPublicId)
      selfAppliedApi.interceptors.request.eject(reqInterceptorSelfAppliedId)
      selfAppliedApi.interceptors.response.eject(resInterceptorSelfAppliedId)
    }
  }, [auth])

  return children
}
